import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import {
    Col, Row, Container,
    Tab,
    Nav,
} from 'react-bootstrap';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { PatternFormat } from 'react-number-format';
import Welcome from '../../components/Welcome';
import BlockUISpinner from '../../components/BlockUISpinner';
import {
    getDashboardNotification,
    clearDashboardNotification,
    setDashboardNotification,
    getDashboardInfoNotification,
    setDashboardInfoNotification,
    clearDashboardInfoNotification,
} from '../../storage/notification';
import NotificationMessage from '../../components/Alert/NotificationMessage';
import useHtmlTitle from '../../components/Utilities/useHtmlTitle';
import useBodyClass from '../../components/Utilities/useBodyClass';
import useAccountContext from '../../authentication/hooks';
import StandardPathway from '../../components/tiles/StandardPathway';
import {
    DashboardClient, DashboardItemDto, PagedListOfDashboardItemDto, ProblemDetails,
    StatusEnumDto,
} from '../../api/web-api-client';
import { tokenRequest } from '../../authentication/authConfig';
import AppLogger from '../../instrumentation/AppLogger';
import getUnexpectedErrorRoute from '../common/errorRoutes';
import { HttpStatusCode } from '../../types';
import { DashBoardNotifications } from '../common/dashboardNotifications';
import SearchFilter from '../../components/SearchFilter';
import RequestItem from '../../components/RequestList/requestItem';
import InstrumentItem from '../../components/RequestList/instrumentItem';
import NoRequests from '../../components/RequestList/noRequests';
import { DashboardTab, UserProfile } from '../../components/SearchFilter/types';
import CustomPagination from '../../components/Pagination';
import CustomPaginationHeader from '../../components/PaginationHeader';
import { defaultFilter } from '../common/constants';
import { mapToUserProfile } from '../common/helperFunctions';
import SessionStorageCache from '../../storage/sessionStorageCache';
import { DashboardItemStatus } from '../common/enums';

// TS Move this to a constants file if we need this setting app wide
const DEFAULT_DASHBOARD_PAGESIZE = 10;
const DEFAULT_SEARCH_PLACEHOLDER = 'Search by manufacturer, model, serial...';

const showDashboardMessage = (message: JSX.Element | null) => (
    <>
        {message && (
            <Container>
                <Row>
                    <Col>
                        {message}
                    </Col>
                </Row>
            </Container>
        )}
    </>
);

const setNotification = () => {
    const dashboardNotification = getDashboardNotification();
    return (
        dashboardNotification
            ? (
                <NotificationMessage
                    id='notif-message-1'
                    canClose
                    onClose={clearDashboardNotification}
                    {...dashboardNotification}
                />
            )
            : null);
};

const setInfoNotification = () => {
    const dashboardInfoNotification = getDashboardInfoNotification();
    return (
        dashboardInfoNotification
            ? (
                <NotificationMessage
                    id='notif-info-message-2'
                    canClose
                    onClose={clearDashboardInfoNotification}
                    {...dashboardInfoNotification}
                />
            )
            : null);
};

const handleAlertScroll = () => {
    window.scrollTo(0, 0);
    setTimeout(() => {
        const el: HTMLElement = document.querySelector(('[id^="#notif-"]')) as HTMLElement;
        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        el?.focus();
    }, 100);
};

const handlePaginationScroll = () => {
    const titleElement = document.getElementById('dash-type-title');
    titleElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const fetchRequestsByTab = async (
    tab: DashboardTab,
    client: DashboardClient,
    sortOrder: string,
    currentPage: number,
    pageSize: number,
    accountDetailsCrmGuid?: string,
    filterSearchText?: string,
    actualYear?: number,
    actualStatus?: StatusEnumDto,
    signal?:AbortSignal,
): Promise<PagedListOfDashboardItemDto> => {
    switch (tab) {
        case DashboardTab.Drafts:
            return client.getDashboardDraftsByPortalID(
                accountDetailsCrmGuid,
                actualYear,
                actualStatus,
                sortOrder,
                filterSearchText,
                currentPage,
                pageSize,
                signal,
            );
        case DashboardTab.Requests:
            return client.getDashboardQuotesByPortalID(
                accountDetailsCrmGuid,
                actualYear,
                actualStatus,
                sortOrder,
                filterSearchText,
                currentPage,
                pageSize,
                signal,
            );
        case DashboardTab.Instruments:
            return client.getDashboardArtefactsByPortalID(
                accountDetailsCrmGuid,
                actualYear,
                actualStatus,
                sortOrder,
                filterSearchText,
                currentPage,
                pageSize,
                signal,
            );
        default:
            throw new Error(`Unsupported tab: ${tab}`);
    }
};

const Dashboard = () => {
    const { inProgress, accounts, instance } = useMsal();
    const [requests, setRequests] = useState<DashboardItemDto[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isDataLoading, setIsDataLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [reload, setReload] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const [scrollToTop, setScrollToTop] = useState(false);
    const accountContext = useAccountContext();
    const accountDetails = accountContext?.details;
    const [errorStatus, setErrorStatus] = useState({
        hasError: false, status: 0, forbidden: false, noThirdPartyAccess: false,
    });

    const [initialFilters, setInitialFilters] = useState<UserProfile | undefined>(undefined);
    const dashboardMessage = setNotification();
    const dashboardInfoMessage = setInfoNotification();
    const orgName = accountContext?.details?.targetOrganisation?.targetOrganisationName as string;

    // Paging
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [activeTab, setActiveTab] = useState(DashboardTab.Drafts);
    const [totalCount, setTotalCount] = useState(0);
    const [searchPlaceholder, setSearchPlaceholder] = useState(DEFAULT_SEARCH_PLACEHOLDER);

    const onShowBranchSelectorClick = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        e.preventDefault();
        accountContext?.setShowBranchSelector(true);
    };

    const changePlaceholderForSearchBox = (tab: DashboardTab) => {
        switch (tab) {
            case DashboardTab.Drafts:
                setSearchPlaceholder(DEFAULT_SEARCH_PLACEHOLDER);
                break;
            case DashboardTab.Requests:
                setSearchPlaceholder(DEFAULT_SEARCH_PLACEHOLDER);
                break;
            case DashboardTab.Instruments:
                setSearchPlaceholder('Search by instrument/artefact...');
                break;
            default:
                setSearchPlaceholder(DEFAULT_SEARCH_PLACEHOLDER);
        }
    };

    // TS - This is to fix azure board issue 489452
    // This will have become less of a problem with the new paginated tabs
    const checkAcceptedQuoteStatus = (requestsResponse: PagedListOfDashboardItemDto) => {
        // Grab the latest updated request with the quote that has been successfully updated as far as the api is concerned
        const newlyAcceptedQuoteId = SessionStorageCache().getItem('accepted-quote-id');
        if (newlyAcceptedQuoteId) {
            // Get the RFQ
            const requestResponse = requestsResponse.items!.find((x) => x.referenceId === newlyAcceptedQuoteId);
            // If the status is still saying quote available then we need to update it to accepted
            if (requestResponse && requestResponse.status === DashboardItemStatus.QuoteAvailable) {
                // Update the status to accepted this should rectifiy the incorrect status but on reload this
                // block should't be hit again as the status will be correct
                requestResponse.status = DashboardItemStatus.QuoteAccepted;
                requestResponse.quote!.artefactName = requestResponse.requestForQuote?.artefactName;
                requestResponse.lastUpdated = new Date();

                // Move the card to the top of the list
                const index = requestsResponse.items!.findIndex((x) => x.referenceId === newlyAcceptedQuoteId);
                if (index !== -1) {
                    const [updatedItem] = requestsResponse.items!.splice(index, 1);
                    requestsResponse.items!.unshift(updatedItem);
                }

                // Lets set the session for quote item viewing if this is set we have an outdated status
                SessionStorageCache().setItem(requestResponse.referenceId!, 'view-quote-id');
            }
            SessionStorageCache().removeItem('accepted-quote-id');
        }
    };

    useHtmlTitle('Dashboard | NMI Services portal');
    useBodyClass('dashboard');

    const saveUserProfile = (userProfile: UserProfile) => {
        const profile = {
            filterYearType: userProfile.filterYearType,
            filterStatusType: userProfile.filterStatusType,
            filtersChanged: userProfile.filtersChanged,
            filterSortOrder: userProfile.filterSortOrder,
            filterCurrentPage: userProfile.filterCurrentPage,
            filterActiveTab: userProfile.filterActiveTab,
            filterSearchText: userProfile.filterSearchText,
        };
        accountContext?.setUserProfile(profile);
    };

    const changeTab = (tab: DashboardTab) => {
        setRequests([]);
        changePlaceholderForSearchBox(tab);
        setActiveTab(tab);
        setCurrentPage(1);
        if (initialFilters) {
            setInitialFilters((prevState) => ({
                ...prevState,
                filterActiveTab: tab,
                filterCurrentPage: 1,
            }));

            const profile = {
                filterYearType: initialFilters?.filterYearType,
                filterStatusType: initialFilters?.filterStatusType,
                filtersChanged: initialFilters?.filtersChanged,
                filterSortOrder: initialFilters?.filterSortOrder,
                filterCurrentPage: 1,
                filterActiveTab: tab,
                filterSearchText: initialFilters?.filterSearchText,
            };
            saveUserProfile(profile);
        }
    };

    const changePage = (page: number) => {
        setCurrentPage(page);
        if (initialFilters) {
            setInitialFilters((prevState) => ({
                ...prevState,
                filterCurrentPage: page,
            }));

            const profile = {
                filterYearType: initialFilters?.filterYearType,
                filterStatusType: initialFilters?.filterStatusType,
                filtersChanged: initialFilters?.filtersChanged,
                filterSortOrder: initialFilters?.filterSortOrder,
                filterCurrentPage: page,
                filterActiveTab: initialFilters?.filterActiveTab,
                filterSearchText: initialFilters?.filterSearchText,
            };
            saveUserProfile(profile);
        }
    };

    // Set values from the userprofile
    useEffect(() => {
        if (accountDetails?.userProfile) {
            const p = mapToUserProfile(accountDetails?.userProfile);
            setActiveTab(p.filterActiveTab!);
            changePlaceholderForSearchBox(p.filterActiveTab!);
            setCurrentPage(p.filterCurrentPage!);

            // Only need to do this on first load
            if (initialFilters === undefined) setInitialFilters(p);
        }
    }, [accountDetails?.userProfile]);

    useEffect(() => {
        // setIsDataLoading(true); // Dashboard data
        const loadDataForDisplay = async () => {
            if (accountContext
                && accountDetails
                && accountDetails?.organisationCRMGuid
                && initialFilters) {
                if (inProgress === InteractionStatus.None && accounts.length > 0) {
                    try {
                        AppLogger.verbose('Dashboard.loadDataForDisplay', accountContext.details);
                        const client = new DashboardClient();
                        const tokenResult = await instance.acquireTokenSilent({
                            ...tokenRequest,
                            account: accounts[0],
                        });
                        client.setAuthToken(tokenResult.accessToken);
                        setErrorStatus((prevState) => ({ ...prevState, hasError: false }));
                        setIsModalOpen(
                            // List and check all Actionable modals across portal app
                            (!!accountDetails?.showBranchSelector
                            || !!accountDetails?.showRFQDeleteModal
                            || !accountDetails?.userAcceptedTermsOfUse),
                        );
                        setIsDataLoading(true);
                        const actualYear = initialFilters.filterYearType === defaultFilter.filterYearType
                            ? undefined
                            : parseInt(initialFilters.filterYearType!, 10);
                        const actualStatus = initialFilters.filterStatusType === defaultFilter.filterStatusType
                            ? undefined
                            : initialFilters.filterStatusType as StatusEnumDto;

                        const requestsResponse = await fetchRequestsByTab(
                            initialFilters.filterActiveTab!,
                            client,
                            'descending', // TS whats up here are we changing this?
                            initialFilters.filterCurrentPage!,
                            DEFAULT_DASHBOARD_PAGESIZE, // TS should we add a pagesize dropdown in the future?
                            accountDetails?.organisationCRMGuid,
                            initialFilters.filterSearchText,
                            actualYear,
                            actualStatus,
                            undefined,
                        );

                        checkAcceptedQuoteStatus(requestsResponse);
                        changePlaceholderForSearchBox(activeTab);
                        setRequests(requestsResponse.items!);
                        setCurrentPage(requestsResponse.currentPage!);
                        setTotalPages(requestsResponse.totalPages!);
                        setTotalCount(requestsResponse.totalCount!);
                        setShowInfo(false);
                    } catch (error) {
                        AppLogger.error('Failed to load dashboard.', error as Error);
                        const problemDetails = error as ProblemDetails;
                        if (problemDetails.status! > 0) setErrorStatus((prevState) => ({ ...prevState, status: problemDetails.status! }));
                        if (problemDetails.status === HttpStatusCode.Forbidden
                            && problemDetails.title
                            && problemDetails.title.includes('No third-party access')) {
                            setErrorStatus((prevState) => ({ ...prevState, noThirdPartyAccess: true }));
                        } else if (problemDetails.status === HttpStatusCode.Forbidden) {
                            setErrorStatus((prevState) => ({ ...prevState, forbidden: true }));
                        } else {
                            setErrorStatus((prevState) => ({ ...prevState, hasError: true }));
                        }
                        AppLogger.info('Dashboard load error values', errorStatus);
                    } finally {
                        setIsDataLoading(false);
                        setReload(false);
                    }
                }
            }
        };
        loadDataForDisplay();

        if (scrollToTop) {
            handleAlertScroll();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accountContext?.details?.organisationCRMGuid,
        instance,
        inProgress,
        reload,
        scrollToTop,
        initialFilters]);

    const renderDashTypeTitle = () => (
        <>
            <Row className='mb-2'>
                <Col className='d-flex justify-content-between align-items-md-center'>
                    <h2 id='dash-type-title' className='mb-0 me-3'>Manage Testing and Calibration</h2>
                    <span className='text-end'>
                        <Link
                            data-testid='new-request-button'
                            to='/services-we-offer'
                            className='btn btn-primary text-nowrap'
                        >
                            <i className='icon-plus me-md-2' aria-hidden='true' role='presentation' />
                            <span className='d-none d-md-inline-block'>
                                New request
                            </span>
                        </Link>
                    </span>
                </Col>
            </Row>
            <Row className='mb-2'>
                {initialFilters && (
                    <SearchFilter
                        setCurrentPage={setCurrentPage}
                        initialFilters={initialFilters}
                        setInitialFilters={setInitialFilters}
                        placeholder={searchPlaceholder}
                        className='mb-md-1'
                    />
                )}
            </Row>
        </>
    );

    const showRequestList = (trequests: DashboardItemDto[], userAcceptedTermsOfUse?: boolean) => (
        <>
            {(requests !== undefined
                    && requests?.length > 0
                    && userAcceptedTermsOfUse) // Check user has accepted terms of use before showing the request list
                ? (
                    <ol className='list-unstyled'>
                        {trequests.map((r: DashboardItemDto) => (
                            <RequestItem request={r} key={r.referenceId} />
                        ))}
                    </ol>
                )
                : !isDataLoading && (
                    <NoRequests />
                )}
        </>
    );

    const showInstrumentList = (trequests: DashboardItemDto[], userAcceptedTermsOfUse?: boolean) => (
        <>
            {(requests !== undefined
                    && requests?.length > 0
                    && userAcceptedTermsOfUse) // Check user has accepted terms of use before showing the request list
                ? (
                    <ol className='list-unstyled'>
                        {trequests.map((r: DashboardItemDto) => (
                            <InstrumentItem request={r} key={r.referenceId} />
                        ))}
                    </ol>
                )
                : !isDataLoading && (
                    <NoRequests />
                )}
        </>
    );

    const requestTabContent = (
        <Row className='mb-4'>
            <Col
                {...{ inert: isModalOpen ? '' : undefined }}
                aria-busy={!isModalOpen && isDataLoading}
                aria-live={isModalOpen ? undefined : 'polite'}
            >
                {!isModalOpen && isDataLoading
                    ? (
                        <BlockUISpinner partial>
                            <p>Loading data...</p>
                        </BlockUISpinner>
                    )
                    : (
                        <>
                            <div className='dashboard-items mb-5'>
                                {showRequestList(requests, accountDetails?.userAcceptedTermsOfUse)}
                            </div>
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page: number) => {
                                    changePage(page);
                                    handlePaginationScroll();
                                }}
                                containerClassName='d-flex justify-content-center'
                            />
                        </>
                    )}
            </Col>
        </Row>
    );

    const instrumentTabContent = (
        <Row className='mb-4'>
            <Col
                {...{ inert: isModalOpen ? '' : undefined }}
                aria-busy={!isModalOpen && isDataLoading}
                aria-live={isModalOpen ? undefined : 'polite'}
            >
                {!isModalOpen && isDataLoading
                    ? (
                        <BlockUISpinner partial>
                            <p>Loading data...</p>
                        </BlockUISpinner>
                    )
                    : (
                        <>
                            <div className='dashboard-items mb-5'>
                                {showInstrumentList(requests, accountDetails?.userAcceptedTermsOfUse)}
                            </div>
                            <CustomPagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={(page: number) => {
                                    changePage(page);
                                    handlePaginationScroll();
                                }}
                                containerClassName='d-flex justify-content-center'
                            />
                        </>
                    )}
            </Col>
        </Row>
    );

    const renderDashTCSubView = () => (
        <Row className='mb-3'>
            <Col>
                <Tab.Container
                    id='dashboard-type'
                    activeKey={activeTab}
                >
                    <Nav
                        as='ul'
                        variant='underline'
                        className='align-items-center overflow-x-auto no-scrollbars mb-md-3'
                        aria-label='Select your dashboard view'
                        activeKey={activeTab}
                    >
                        <span className='d-flex flex-nowrap'>
                            <Nav.Item id={DashboardTab.Drafts} as='li' role='presentation' onClick={() => changeTab(DashboardTab.Drafts)}>
                                <Nav.Link eventKey={DashboardTab.Drafts} className='px-3'>Drafts</Nav.Link>
                            </Nav.Item>
                            <Nav.Item id={DashboardTab.Requests} as='li' role='presentation' onClick={() => changeTab(DashboardTab.Requests)}>
                                <Nav.Link eventKey={DashboardTab.Requests} className='px-3'>Requests</Nav.Link>
                            </Nav.Item>
                            <Nav.Item id={DashboardTab.Instruments} as='li' role='presentation' onClick={() => changeTab(DashboardTab.Instruments)}>
                                <Nav.Link eventKey={DashboardTab.Instruments} className='px-3'>Instrument/artefacts</Nav.Link>
                            </Nav.Item>
                        </span>
                        {!isDataLoading && (
                            <span className='d-flex justify-content-end ms-auto p-2 ps-4 small text-nowrap'>
                                <CustomPaginationHeader currentPage={currentPage} totalCount={totalCount} pageSize={DEFAULT_DASHBOARD_PAGESIZE} />
                            </span>
                        )}
                    </Nav>
                    <Tab.Content>
                        <Tab.Pane eventKey={DashboardTab.Drafts} tabIndex={0}>
                            {requestTabContent}
                        </Tab.Pane>
                        <Tab.Pane eventKey={DashboardTab.Requests} tabIndex={0}>
                            {requestTabContent}
                        </Tab.Pane>
                        <Tab.Pane eventKey={DashboardTab.Instruments} tabIndex={0}>
                            {instrumentTabContent}
                        </Tab.Pane>
                    </Tab.Content>
                </Tab.Container>
            </Col>
        </Row>
    );

    if (errorStatus.forbidden) {
        const message = DashBoardNotifications.getForbiddenNotification();
        setDashboardNotification(message);
    }

    if (errorStatus.noThirdPartyAccess) {
        const message = DashBoardNotifications.getThirdPartyAccessNotification(orgName);
        setDashboardNotification(message);
        if (accountContext) {
            accountContext.setTargetOrganisation(
                accountContext?.details?.abn ?? '',
                accountContext?.details?.organisation ?? '',
            );
        }
        setErrorStatus((prevState) => ({ ...prevState, noThirdPartyAccess: false }));
    }

    if (errorStatus.hasError) {
        return <Navigate to={getUnexpectedErrorRoute(errorStatus.status)} />;
    }

    if (showInfo) {
        const message = DashBoardNotifications.getReportFormsGeneratedNotification();
        setDashboardInfoNotification(message);
    } else {
        clearDashboardInfoNotification();
    }

    return (
        <>
            {isLoading && (
                <BlockUISpinner>
                    <p>Loading...</p>
                </BlockUISpinner>
            )}
            <div aria-busy={isLoading} aria-live={isModalOpen ? undefined : 'polite'}>
                <Welcome />
            </div>
            {showDashboardMessage(dashboardMessage)}
            {showDashboardMessage(dashboardInfoMessage)}
            <Container>
                <Row className='mb-5'>
                    <Col aria-busy={isLoading} aria-live={isModalOpen ? undefined : 'polite'}>
                        {!!accountContext?.details && (
                            <>
                                <h2 className='h1 text-body mb-3' aria-describedby='orgABN'>
                                    <span className='d-block mb-1 fs-5 fw-normal'>
                                        {'Currently managing '}
                                    </span>
                                    <span className='d-block text-break'>
                                        {!!(accountContext?.details?.organisation) && (`${accountContext?.details?.organisation}`)}
                                    </span>
                                    <span className='d-block text-break'>
                                        {!!(accountContext?.details?.trading) && (`${accountContext?.details?.trading}`)}
                                        {!!(accountContext?.details?.branch) && (` - ${accountContext?.details?.branch}`)}
                                    </span>
                                </h2>
                                {!!(accountContext?.details?.abn) && (
                                    <>
                                        <span id='orgABN' className='d-inline-block me-3 fw-normal'>
                                            {'ABN: '}
                                            <PatternFormat
                                                value={accountContext?.details?.abn}
                                                displayType='text'
                                                format='## ### ### ###'
                                                aria-hidden='true'
                                                role='presentation'
                                                valueIsNumericString={false}
                                            />
                                        </span>
                                        <span className='visually-hidden'>
                                            {accountContext?.details?.abn?.split('').join(' ')}
                                        </span>
                                    </>
                                )}
                                <Link
                                    data-testid='open-manage-branch-division-button'
                                    role='button'
                                    onClick={onShowBranchSelectorClick}
                                    to='#0'
                                    className='text-nowrap'
                                >
                                    Add or manage branch/location
                                </Link>
                            </>
                        )}
                    </Col>
                </Row>
                {renderDashTypeTitle()}
                {renderDashTCSubView()}

                <Row className='mb-5'>
                    <h2 className='pt-md-4 mb-3'>Quick links</h2>
                    <Col md>
                        <StandardPathway
                            type='internal'
                            title='Services we offer'
                            linkDescription=' '
                            bodyText='The National Measurement Institute (NMI) offers a range of service. Find out more...'
                            to='/services-we-offer'
                        />
                    </Col>
                    <Col md>
                        <StandardPathway
                            type='internal'
                            title='Help guide'
                            linkDescription=' '
                            bodyText='View help guides for common issues and troubleshooting tips'
                            to='/help-guide'
                        />
                    </Col>
                    <Col md>
                        {/* <StandardPathway
                            type='external'
                            title='Get help'
                            linkDescription=' '
                            bodyText='See a list of registered consultants and contact National Measurement Institute (NMI)'
                            linkHref='https://www.measurement.gov.au'
                            target='_blank'
                        /> */}
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default Dashboard;
